import { 
  GoogleAuthProvider, 
  signInWithPopup, 
  signInWithRedirect,
  getRedirectResult,
  signOut as firebaseSignOut,
  User as FirebaseUser,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { User } from '../types';

const googleProvider = new GoogleAuthProvider();

// Configure Google provider to get additional scopes if needed
googleProvider.addScope('email');
googleProvider.addScope('profile');

// Detect if we're on mobile or in a cross-origin scenario
const isMobileOrCrossOrigin = () => {
  const userAgent = navigator.userAgent;
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const isNetworkAccess = !isLocalhost;
  
  return isMobile || isNetworkAccess;
};

export const authService = {
  // Sign in with Google
  signInWithGoogle: async (): Promise<User> => {
    try {
      // For network access (cross-origin), always try popup first, then fallback to redirect
      if (isMobileOrCrossOrigin()) {
        console.log('🔄 Cross-origin detected - trying popup first, then redirect fallback');
        
        try {
          // Try popup first even on mobile - sometimes it works
          console.log('🪟 Attempting popup method...');
          const result = await signInWithPopup(auth, googleProvider);
          const firebaseUser = result.user;
          
          console.log('✅ Popup authentication successful:', firebaseUser.email);
          const user = await createOrUpdateUserDocument(firebaseUser);
          return user;
        } catch (popupError: any) {
          console.log('❌ Popup failed, falling back to redirect:', popupError.message);
          
          // Store redirect attempt in localStorage
          localStorage.setItem('auth_redirect_pending', 'true');
          localStorage.setItem('auth_redirect_timestamp', Date.now().toString());
          
          await signInWithRedirect(auth, googleProvider);
          // Note: The actual result will be handled by checkRedirectResult
          // This method will cause a page redirect, so it won't return immediately
          throw new Error('REDIRECT_IN_PROGRESS'); // This is expected behavior
        }
      } else {
        console.log('🪟 Using popup method for authentication');
        const result = await signInWithPopup(auth, googleProvider);
        const firebaseUser = result.user;
        
        // Create or update user document in Firestore
        const user = await createOrUpdateUserDocument(firebaseUser);
        return user;
      }
    } catch (error: any) {
      if (error.message === 'REDIRECT_IN_PROGRESS') {
        throw error; // Re-throw this expected "error"
      }
      console.error('Error signing in with Google:', error);
      throw new Error(error.message || 'Failed to sign in with Google');
    }
  },

  // Check for redirect result (call this on app initialization)
  checkRedirectResult: async (): Promise<User | null> => {
    try {
      console.log('🔍 Checking for redirect result...');
      
      // Check if we were expecting a redirect
      const redirectPending = localStorage.getItem('auth_redirect_pending');
      const redirectTimestamp = localStorage.getItem('auth_redirect_timestamp');
      
      if (redirectPending === 'true') {
        console.log('📋 Found pending redirect authentication...');
        
        // Clear the pending flag immediately to avoid loops
        localStorage.removeItem('auth_redirect_pending');
        localStorage.removeItem('auth_redirect_timestamp');
        
        // Wait longer for Firebase to process the redirect
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        const result = await getRedirectResult(auth);
        console.log('📋 Redirect result:', result);
        
        if (result && result.user) {
          console.log('✅ Redirect authentication successful for user:', result.user.email);
          const user = await createOrUpdateUserDocument(result.user);
          console.log('👤 User document created/updated:', user);
          return user;
        } else {
          console.log('⚠️ Expected redirect result but got null - checking current auth state');
          
          // Wait a bit more and check again
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Sometimes the redirect completes but getRedirectResult returns null
          // Check if there's a current user
          if (auth.currentUser) {
            console.log('✅ Found authenticated user after redirect:', auth.currentUser.email);
            const user = await createOrUpdateUserDocument(auth.currentUser);
            return user;
          }
          
          console.log('❌ No authenticated user found after redirect - authentication may have failed');
        }
      }
      
      // Regular check without expecting redirect
      const result = await getRedirectResult(auth);
      if (result && result.user) {
        console.log('✅ Unexpected redirect authentication found:', result.user.email);
        const user = await createOrUpdateUserDocument(result.user);
        return user;
      }
      
      console.log('ℹ️ No redirect result found (user may have accessed directly)');
      return null;
    } catch (error: any) {
      console.error('❌ Error handling redirect result:', error);
      
      // Clear pending redirect on error
      localStorage.removeItem('auth_redirect_pending');
      localStorage.removeItem('auth_redirect_timestamp');
      
      // Check if it's just because there was no redirect
      if (error.code === 'auth/no-auth-event') {
        console.log('ℹ️ No auth event found - user accessed directly');
        return null;
      }
      
      throw new Error(error.message || 'Failed to handle authentication redirect');
    }
  },

  // Sign out
  signOut: async (): Promise<void> => {
    try {
      await firebaseSignOut(auth);
    } catch (error: any) {
      console.error('Error signing out:', error);
      throw new Error('Failed to sign out');
    }
  },

  // Get current user
  getCurrentUser: (): Promise<User | null> => {
    return new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        unsubscribe();
        if (firebaseUser) {
          try {
            const user = await getUserFromFirestore(firebaseUser.uid);
            resolve(user);
          } catch (error) {
            console.error('Error getting user data:', error);
            resolve(null);
          }
        } else {
          resolve(null);
        }
      });
    });
  },

  // Listen to auth state changes
  onAuthStateChanged: (callback: (user: User | null) => void) => {
    return onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('🔔 Auth state change detected:', firebaseUser ? `${firebaseUser.email} (${firebaseUser.uid})` : 'null');
      
      if (firebaseUser) {
        try {
          // Always create/update user document when user is authenticated
          const user = await createOrUpdateUserDocument(firebaseUser);
          console.log('✅ User document processed for auth state change:', user.email);
          callback(user);
        } catch (error) {
          console.error('❌ Error processing user document in auth state change:', error);
          callback(null);
        }
      } else {
        console.log('🚪 User signed out');
        callback(null);
      }
    });
  }
};

// Helper function to create or update user document in Firestore
async function createOrUpdateUserDocument(firebaseUser: FirebaseUser): Promise<User> {
  const userRef = doc(db, 'users', firebaseUser.uid);
  const userSnap = await getDoc(userRef);

  const userData: User = {
    id: firebaseUser.uid,
    email: firebaseUser.email || '',
    displayName: firebaseUser.displayName || '',
    photoURL: firebaseUser.photoURL || undefined,
    createdAt: userSnap.exists() ? userSnap.data().createdAt.toDate() : new Date(),
    lastLoginAt: new Date(),
  };

  // Update or create user document
  await setDoc(userRef, {
    ...userData,
    lastLoginAt: serverTimestamp(),
    ...(userSnap.exists() ? {} : { createdAt: serverTimestamp() })
  }, { merge: true });

  return userData;
}

// Helper function to get user data from Firestore
async function getUserFromFirestore(uid: string): Promise<User | null> {
  try {
    const userRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      const data = userSnap.data();
      return {
        id: uid,
        email: data.email,
        displayName: data.displayName,
        photoURL: data.photoURL,
        createdAt: data.createdAt?.toDate() || new Date(),
        lastLoginAt: data.lastLoginAt?.toDate() || new Date(),
      };
    }
    return null;
  } catch (error) {
    console.error('Error fetching user data:', error);
    return null;
  }
}